import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

interface Account { id: string; name: string; region: string }
interface CriteriaProfile { id: string; name: string; description: string | null; is_builtin: number; conditions: string; created_at: string }
interface AutomationRule {
  id: string; name: string; description: string | null; algorithm_type: string;
  is_active: number; priority: number; criteria_profile_id: string | null;
  criteria_name: string | null; days_to_analyze: number; days_to_ignore: number;
  config: string; ad_type: string; live_count: number; preview_count: number;
  created_at: string;
}
interface LogEntry {
  id: string; algorithm_type: string; rule_name: string; campaign_name: string;
  keyword_text: string | null; search_term: string | null; change_type: string;
  old_value: string; new_value: string; mode: string; condition_values: string;
  details: string; created_at: string;
}
interface Objective {
  id: string; name: string; description: string | null; objective_type: string;
  is_builtin: number; is_active: number; rule_count: number; assignment_count: number;
}

const ALGO_LABELS: Record<string, string> = {
  bidding: 'Bidding', negative: 'Negative', import: 'Import', status: 'Status',
  budget: 'Budget', dayparting: 'Dayparting', revive: 'Revive',
  placement: 'Placement', default_bid: 'Default Bid', negative_word: 'Neg. Word',
};
const ALGO_COLORS: Record<string, string> = {
  bidding: 'blue', negative: 'red', import: 'green', status: 'yellow',
  budget: 'purple', dayparting: 'orange', revive: 'teal',
  placement: 'indigo', default_bid: 'gray', negative_word: 'pink',
};

type TabKey = 'rules' | 'profiles' | 'objectives' | 'log';

export function PpcAutomation() {
  const [accountId, setAccountId] = useState('');
  const [tab, setTab] = useState<TabKey>('rules');
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [profiles, setProfiles] = useState<CriteriaProfile[]>([]);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setStatusData] = useState<Record<string, unknown> | null>(null);

  // Filters
  const [algoFilter, setAlgoFilter] = useState('');
  const [logMode, setLogMode] = useState<'' | 'preview' | 'live'>('');
  const [logDays, setLogDays] = useState(7);

  // Forms
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);

  useEffect(() => {
    api.get<Account[]>('/accounts').then(accts => {
      const active = accts.filter((a: Account & { is_active?: number }) => a.is_active !== 0);
      if (active.length > 0) setAccountId(active[0].id);
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError('');
    try {
      const [rulesRes, profilesRes, objRes, statusRes] = await Promise.all([
        api.get<{ rules: AutomationRule[] }>(`/automation/rules?accountId=${accountId}${algoFilter ? `&algorithmType=${algoFilter}` : ''}`),
        api.get<{ profiles: CriteriaProfile[] }>(`/automation/criteria-profiles?accountId=${accountId}`),
        api.get<{ objectives: Objective[] }>(`/automation/objectives?accountId=${accountId}`),
        api.get<Record<string, unknown>>(`/automation/status?accountId=${accountId}`),
      ]);
      setRules(rulesRes.rules);
      setProfiles(profilesRes.profiles);
      setObjectives(objRes.objectives);
      setStatusData(statusRes);
    } catch (e) { setError(String(e)); }
    setLoading(false);
  }, [accountId, algoFilter]);

  const loadLog = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get<{ entries: LogEntry[] }>(
        `/automation/log?accountId=${accountId}&daysBack=${logDays}${logMode ? `&mode=${logMode}` : ''}${algoFilter ? `&algorithmType=${algoFilter}` : ''}`
      );
      setLogEntries(res.entries);
    } catch (e) { setError(String(e)); }
  }, [accountId, logDays, logMode, algoFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (tab === 'log') loadLog(); }, [tab, loadLog]);

  async function toggleRule(rule: AutomationRule) {
    await api.put(`/automation/rules/${rule.id}`, { isActive: rule.is_active !== 1 });
    loadData();
  }

  async function deleteRule(id: string) {
    if (!confirm('Delete this rule?')) return;
    await api.delete(`/automation/rules/${id}`);
    loadData();
  }

  async function deleteProfile(id: string) {
    await api.delete(`/automation/criteria-profiles/${id}`);
    loadData();
  }

  // Preview is triggered via MCP tools (Claude) or the sync worker trigger endpoint.
  // The dashboard monitors results in the Change Log tab.

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'rules', label: 'Automation Rules' },
    { key: 'profiles', label: 'Criteria Profiles' },
    { key: 'objectives', label: 'Objectives' },
    { key: 'log', label: 'Change Log' },
  ];

  // Status cards
  const activeRules = rules.filter(r => r.is_active === 1).length;
  const totalExecutions = rules.reduce((s, r) => s + (r.live_count ?? 0), 0);
  const totalPreviews = rules.reduce((s, r) => s + (r.preview_count ?? 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PPC Automation</h1>
          <p className="mt-1 text-sm text-gray-500">
            AI-powered rules engine — create rules via Claude, validated by Workers AI, monitored here
          </p>
        </div>
        <button onClick={() => setShowRuleForm(true)}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          + New Rule
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {/* Status Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="Active Rules" value={activeRules} total={rules.length} color="blue" />
        <StatusCard label="Criteria Profiles" value={profiles.length} sub={`${profiles.filter(p => p.is_builtin).length} built-in`} color="green" />
        <StatusCard label="Objectives" value={objectives.length} sub={`${objectives.reduce((s, o) => s + o.assignment_count, 0)} assigned`} color="purple" />
        <StatusCard label="Actions (7d)" value={totalExecutions} sub={`${totalPreviews} previewed`} color="orange" />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`border-b-2 pb-2 text-sm font-medium transition-colors ${
                tab === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Algorithm type filter */}
      {(tab === 'rules' || tab === 'log') && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Filter:</span>
          <button onClick={() => setAlgoFilter('')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${!algoFilter ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            All
          </button>
          {Object.entries(ALGO_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setAlgoFilter(key === algoFilter ? '' : key)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                algoFilter === key
                  ? `bg-${ALGO_COLORS[key]}-100 text-${ALGO_COLORS[key]}-700`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="py-8 text-center text-gray-500">Loading...</div>}

      {/* Rules Tab */}
      {tab === 'rules' && !loading && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Active</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Criteria</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Priority</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Lookback</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Executions</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rules.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                  No automation rules yet. Create rules via Claude/ChatGPT MCP tools or click "+ New Rule" above.
                </td></tr>
              )}
              {rules.map(r => {
                const color = ALGO_COLORS[r.algorithm_type] ?? 'gray';
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <button onClick={() => toggleRule(r)} title={r.is_active ? 'Active — click to pause' : 'Paused — click to activate'}
                        className={`h-4 w-8 rounded-full transition-colors ${r.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <span className={`block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${r.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{r.name}</div>
                      {r.description && <div className="text-xs text-gray-400">{r.description}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-${color}-100 text-${color}-700`}>
                        {ALGO_LABELS[r.algorithm_type] ?? r.algorithm_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.criteria_name ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.priority}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.days_to_analyze}d (-{r.days_to_ignore}d)</td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-gray-800">{r.live_count}</span>
                      <span className="text-gray-400 ml-1">/ {r.preview_count}p</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => { setEditingRule(r); setShowRuleForm(true); }}
                        className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 mr-1">
                        Edit
                      </button>
                      <button onClick={() => deleteRule(r.id)}
                        className="rounded border border-red-200 px-2 py-0.5 text-xs text-red-600 hover:bg-red-50">
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Criteria Profiles Tab */}
      {tab === 'profiles' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowProfileForm(true)}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
              + New Profile
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {profiles.map(p => {
              const conditions = JSON.parse(p.conditions || '[]') as Array<{ metric: string; operator: string; value: number; value2?: number }>;
              return (
                <div key={p.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-800">{p.name}</h3>
                      {p.description && <p className="mt-0.5 text-xs text-gray-500">{p.description}</p>}
                    </div>
                    {p.is_builtin ? (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Built-in</span>
                    ) : (
                      <button onClick={() => deleteProfile(p.id)}
                        className="rounded border border-red-200 px-2 py-0.5 text-xs text-red-600 hover:bg-red-50">
                        Delete
                      </button>
                    )}
                  </div>
                  <div className="mt-3 space-y-1">
                    {conditions.map((c, i) => (
                      <div key={i} className="flex items-center gap-1 text-xs">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-700">{c.metric}</span>
                        <span className="text-gray-400">{c.operator}</span>
                        <span className="font-medium text-gray-800">{c.value}{c.value2 !== undefined ? `–${c.value2}` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Objectives Tab */}
      {tab === 'objectives' && !loading && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Rules</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Assignments</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {objectives.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                  No objectives yet. Create objectives via Claude/ChatGPT to bundle multiple rules together.
                </td></tr>
              )}
              {objectives.map(o => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{o.name}</div>
                    {o.description && <div className="text-xs text-gray-400">{o.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{o.objective_type}</td>
                  <td className="px-3 py-2 text-right text-gray-800">{o.rule_count}</td>
                  <td className="px-3 py-2 text-right text-gray-800">{o.assignment_count}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${o.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {o.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Change Log Tab */}
      {tab === 'log' && !loading && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select value={logDays} onChange={e => setLogDays(Number(e.target.value))}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <select value={logMode} onChange={e => setLogMode(e.target.value as '' | 'preview' | 'live')}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
              <option value="">All modes</option>
              <option value="preview">Preview only</option>
              <option value="live">Live only</option>
            </select>
            <button onClick={loadLog}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Refresh
            </button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Time</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Rule</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Target</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Change</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logEntries.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                    No automation activity in this period.
                  </td></tr>
                )}
                {logEntries.map(e => {
                  const color = ALGO_COLORS[e.algorithm_type] ?? 'gray';
                  return (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-${color}-100 text-${color}-700`}>
                          {ALGO_LABELS[e.algorithm_type] ?? e.algorithm_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{e.rule_name}</td>
                      <td className="px-3 py-2">
                        <div className="text-gray-800">{e.campaign_name}</div>
                        {e.keyword_text && <div className="text-xs text-gray-500">{e.keyword_text}</div>}
                        {e.search_term && <div className="text-xs text-gray-500 italic">{e.search_term}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-400">{e.old_value || '—'}</span>
                        <span className="mx-1 text-gray-300">&rarr;</span>
                        <span className="font-medium text-gray-800">{e.new_value}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          e.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {e.mode}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rule Create/Edit Modal */}
      {showRuleForm && (
        <RuleForm
          accountId={accountId}
          profiles={profiles}
          editRule={editingRule}
          onClose={() => { setShowRuleForm(false); setEditingRule(null); }}
          onSaved={() => { setShowRuleForm(false); setEditingRule(null); loadData(); }}
        />
      )}

      {/* Profile Create Modal */}
      {showProfileForm && (
        <ProfileForm
          accountId={accountId}
          onClose={() => setShowProfileForm(false)}
          onSaved={() => { setShowProfileForm(false); loadData(); }}
        />
      )}
    </div>
  );
}

// ── Status Card Component ────────────────────────────────────────────────────

function StatusCard({ label, value, total, sub, color }: {
  label: string; value: number; total?: number; sub?: string; color: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`text-2xl font-bold text-${color}-600`}>{value}</span>
        {total !== undefined && <span className="text-sm text-gray-400">/ {total}</span>}
      </div>
      {sub && <div className="mt-0.5 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

// ── Rule Create/Edit Form ────────────────────────────────────────────────────

function RuleForm({ accountId, profiles, editRule, onClose, onSaved }: {
  accountId: string; profiles: CriteriaProfile[];
  editRule: AutomationRule | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(editRule?.name ?? '');
  const [description, setDescription] = useState(editRule?.description ?? '');
  const [algorithmType, setAlgorithmType] = useState(editRule?.algorithm_type ?? 'bidding');
  const [criteriaProfileId, setCriteriaProfileId] = useState(editRule?.criteria_profile_id ?? '');
  const [priority, setPriority] = useState(editRule?.priority ?? 0);
  const [daysToAnalyze, setDaysToAnalyze] = useState(editRule?.days_to_analyze ?? 30);
  const [daysToIgnore, setDaysToIgnore] = useState(editRule?.days_to_ignore ?? 2);
  const [configJson, setConfigJson] = useState(
    editRule ? JSON.stringify(JSON.parse(editRule.config || '{}'), null, 2) : getDefaultConfig('bidding'),
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  async function handleSave() {
    if (!name.trim()) { setFormError('Name is required'); return; }
    let config: Record<string, unknown>;
    try { config = JSON.parse(configJson); } catch { setFormError('Invalid JSON config'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editRule) {
        await api.put(`/automation/rules/${editRule.id}`, {
          name, description, criteriaProfileId: criteriaProfileId || null,
          priority, daysToAnalyze, daysToIgnore, config,
        });
      } else {
        await api.post('/automation/rules', {
          accountId, name, description, algorithmType,
          criteriaProfileId: criteriaProfileId || null,
          priority, daysToAnalyze, daysToIgnore, config,
        });
      }
      onSaved();
    } catch (e) { setFormError(String(e)); }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900">{editRule ? 'Edit Rule' : 'Create Automation Rule'}</h2>
        {formError && <div className="mt-2 rounded-lg bg-red-50 p-2 text-sm text-red-700">{formError}</div>}
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Reduce bids on high ACoS keywords"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
          </div>
          {!editRule && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Algorithm Type</label>
              <select value={algorithmType} onChange={e => { setAlgorithmType(e.target.value); setConfigJson(getDefaultConfig(e.target.value)); }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                {Object.entries(ALGO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Criteria Profile</label>
            <select value={criteriaProfileId} onChange={e => setCriteriaProfileId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
              <option value="">No criteria (always trigger)</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name}{p.is_builtin ? ' (built-in)' : ''}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Priority</label>
              <input type="number" value={priority} onChange={e => setPriority(Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Analyze (days)</label>
              <input type="number" value={daysToAnalyze} onChange={e => setDaysToAnalyze(Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Ignore (days)</label>
              <input type="number" value={daysToIgnore} onChange={e => setDaysToIgnore(Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Config (JSON)</label>
            <textarea value={configJson} onChange={e => setConfigJson(e.target.value)} rows={6}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 font-mono text-xs" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving...' : editRule ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Profile Create Form ──────────────────────────────────────────────────────

function ProfileForm({ accountId, onClose, onSaved }: {
  accountId: string; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [conditions, setConditions] = useState([{ metric: 'acos', operator: '>=', value: 50 }]);
  const [saving, setSaving] = useState(false);

  function addCondition() {
    setConditions([...conditions, { metric: 'clicks', operator: '>=', value: 10 }]);
  }

  function updateCondition(i: number, field: string, val: string | number) {
    const updated = [...conditions];
    (updated[i] as Record<string, unknown>)[field] = field === 'value' ? Number(val) : val;
    setConditions(updated);
  }

  function removeCondition(i: number) {
    setConditions(conditions.filter((_, j) => j !== i));
  }

  async function handleSave() {
    if (!name.trim() || conditions.length === 0) return;
    setSaving(true);
    try {
      await api.post('/automation/criteria-profiles', { accountId, name, description, conditions });
      onSaved();
    } catch { /* noop */ }
    setSaving(false);
  }

  const METRICS = ['acos', 'clicks', 'impressions', 'spend', 'sales', 'orders', 'ctr', 'cvr', 'cpc', 'roas'];
  const OPS = ['>', '<', '>=', '<=', '==', '!=', 'between'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900">Create Criteria Profile</h2>
        <div className="mt-4 space-y-4">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Profile name"
            className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
            className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Conditions (AND logic)</div>
            {conditions.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={c.metric} onChange={e => updateCondition(i, 'metric', e.target.value)}
                  className="rounded-lg border border-gray-300 px-2 py-1 text-sm">
                  {METRICS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={c.operator} onChange={e => updateCondition(i, 'operator', e.target.value)}
                  className="rounded-lg border border-gray-300 px-2 py-1 text-sm">
                  {OPS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <input type="number" value={c.value} onChange={e => updateCondition(i, 'value', e.target.value)}
                  className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm" />
                {conditions.length > 1 && (
                  <button onClick={() => removeCondition(i)} className="text-red-400 hover:text-red-600">&times;</button>
                )}
              </div>
            ))}
            <button onClick={addCondition} className="text-sm text-brand-600 hover:text-brand-700">+ Add condition</button>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Default configs per algorithm type ────────────────────────────────────────

function getDefaultConfig(type: string): string {
  const configs: Record<string, object> = {
    bidding: { action: 'target_acos', targetAcosPct: 25, optimizeUsingRpc: true, maxDecreasePct: 10, maxIncreasePct: 10, minBid: 0.10, maxBid: 3.00, normalizeByPlacement: 'None' },
    negative: { source: 'asin_search_terms', applyAt: 'ad_group', negativeType: 'negative_exact', addToBlacklist: false, ignoreWhitelist: false, scope: 'keyword_and_asin' },
    import: { source: 'sponsored_products', matchType: 'exact', importUnique: true, includePhrases: [], excludePhrases: [] },
    status: { campaignStatus: 'paused', adGroupStatus: 'unchanged' },
    budget: { minBudget: 5, maxBudget: 500, observationDays: 3, dowAdjustments: {}, criteriaAdjustments: [] },
    placement: { placementToAdjust: 'top_of_search', placementPctValue: 50, incrementalValue: 10 },
    revive: { targetAcosPct: 25, maxBidIncreasePct: 10 },
    default_bid: { targetAcosPct: 25, maxDecreasePct: 10, maxIncreasePct: 10 },
    dayparting: {},
    negative_word: {},
  };
  return JSON.stringify(configs[type] ?? {}, null, 2);
}
